import { GAME_COMPLETED_EVENT, GAME_EXITED_EVENT } from '../constants/events';

type HostEvent =
  | { type: typeof GAME_COMPLETED_EVENT; payload: Record<string, unknown> }
  | { type: typeof GAME_EXITED_EVENT; payload: Record<string, unknown> };

type GlobalWithRN = typeof globalThis & {
  ReactNativeWebView?: { postMessage: (message: string) => void };
};

/** True when running inside a React Native WebView or a browser popup opened
 * by a parent tab — the only contexts where `HostBridge.exit` can actually
 * reach a host to hand control back to. Everywhere else (a plain dev tab)
 * closing/posting is a no-op, so callers should fall back to their own
 * behavior instead. */
export function isEmbeddedHost(): boolean {
  const _global = globalThis as GlobalWithRN;
  return Boolean(_global.ReactNativeWebView?.postMessage) || Boolean(globalThis.opener);
}

type HostBridgeContinueOptions = {
  message: string;
  showToast?: (message: string) => void;
  exitDelayMs?: number;
};

const EXIT_DELAY_MS = 5000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

function postMessageToHost(event: HostEvent): void {
  const _global = globalThis as GlobalWithRN;
  const serialized = JSON.stringify(event);

  if (_global.ReactNativeWebView?.postMessage) {
    _global.ReactNativeWebView.postMessage(serialized);
    return;
  }

  if (globalThis.opener) {
    (globalThis.opener as Window).postMessage(event, globalThis.location.origin);
  }
}

function closeWindowSafely(): void {
  if (globalThis.document.visibilityState !== 'visible') return;
  try {
    globalThis.close();
  } catch {
    /* close not always permitted */
  }
}

/**
 * Sends a GAME_COMPLETED event to the host environment:
 *   1. React Native WebView  → postMessage (stringified JSON)
 *   2. Browser popup/new-tab → globalThis.opener.postMessage, then globalThis.close()
 *   3. Standalone fallback   → console.info (no-op for UX)
 */
export const HostBridge = {
  async continue(
    payload: Record<string, unknown>,
    options: HostBridgeContinueOptions,
  ): Promise<void> {
    if (!('window' in globalThis)) return;

    const event: HostEvent = { type: GAME_COMPLETED_EVENT, payload };
    postMessageToHost(event);
    options.showToast?.(options.message);
    await sleep(options.exitDelayMs ?? EXIT_DELAY_MS);
    closeWindowSafely();

    // NOTE : For debugging
    // console.info("[HostBridge] GAME_COMPLETED", payload);
  },

  /**
   * Sends a GAME_EXITED event and closes immediately — used for a manual,
   * mid-game exit (not a real completion), so the host can tell this apart
   * from `continue`'s GAME_COMPLETED. No toast/delay: the debug button this
   * backs has no game-result context to show one with.
   */
  exit(payload: Record<string, unknown> = {}): void {
    if (!('window' in globalThis)) return;

    postMessageToHost({ type: GAME_EXITED_EVENT, payload });
    closeWindowSafely();
  },
} as const;
